import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';
import { cn } from '../../../utils/cn';

/**
 * A collection of primitive components for building popover interfaces using Radix UI.
 * These components provide a flexible foundation for creating accessible popovers.
 *
 * @example
 * ```tsx
 * <PopoverWrapper>
 *   <PopoverTrigger>Click me</PopoverTrigger>
 *   <PopoverContent>
 *     Popover content goes here
 *   </PopoverContent>
 * </PopoverWrapper>
 * ```
 */

/**
 * The root container for the popover component.
 * Manages the open state of the popover.
 */
const PopoverWrapper = PopoverPrimitive.Root;

/**
 * The button that triggers the popover to open/close.
 * Must be nested within a PopoverWrapper.
 */
const PopoverTrigger = PopoverPrimitive.Trigger;

/**
 * Optional anchor element that the popover positions itself relative to.
 * Useful when the trigger and positioning source need to be different elements.
 */
const PopoverAnchor = PopoverPrimitive.Anchor;

/**
 * The content container for the popover.
 *
 * @param props.className - Additional CSS classes to apply to the content
 * @param props.align - Alignment of the popover relative to its trigger ('start' | 'center' | 'end')
 * @param props.sideOffset - Distance in pixels between the trigger and content‰
 *
 * Features:
 * - Animated entrance/exit
 * - Responsive positioning
 * - Customizable appearance through className
 * - Accessible by default
 * - Portal-based rendering
 */
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-72 rounded-xl border border-border bg-white text-sm shadow-md outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { PopoverAnchor, PopoverContent, PopoverTrigger, PopoverWrapper };
